import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  Check,
  X,
  Loader2,
  ExternalLink,
  AlertCircle,
  Lock,
  CreditCard,
  LogOut,
  ArrowUpRight,
  Crown,
  Zap,
  Infinity,
  Calendar,
  RefreshCw,
  Settings,
  Brain,
  Trash2,
  Plus,
  Star,
  HelpCircle,
  MessageCircle,
  Mail,
  Bug,
  FileText,
  ChevronRight,
  ChevronDown,
  Key,
  LayoutGrid,
} from '@/components/icons';
import { useAuth } from '../Auth';
import { signOut, customer, authClient } from '../../lib/auth-client';
import { useTabs } from '../../context/TabsContext';
import { trackSignOut } from '../../lib/posthog';
import { resolveAssetUrl } from '../../lib/assets';
import { ProfilePictureUpload } from '../../features/auth';
import { ApiKeysSection } from './ApiKeysSection';
import {
  setApiKey,
  getApiKey,
  removeApiKey,
  hasApiKey,
  validateApiKeyFormat,
  maskApiKey,
  AI_PROVIDERS,
  type AIProvider,
  getDefaultProvider,
  setDefaultProvider,
  getStoredDefaultProvider,
  getCustomBaseUrl,
  setCustomBaseUrl,
  getCustomModel,
  setCustomModel,
  getCustomModels,
  addCustomModel,
  removeCustomModel,
  getSelectedModel,
  setSelectedModel,
  DEFAULT_CUSTOM_BASE_URL,
} from '../../services/apiKeyService';
import { getShowMinimap, setShowMinimap, getShowGrid, setShowGrid, getSnapToGrid, setSnapToGrid } from '../../lib/editorPrefs';
import { fetchProviderModels } from '../../services/providerModels';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import { Switch, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/beui';

type SettingsSection = 'profile' | 'password' | 'billings' | 'ai' | 'apikeys' | 'support' | 'editor';

interface Subscription {
  id: string;
  status: string;
  currentPeriodEnd?: string;
  productId: string;
}

interface SubscriptionStatus {
  subscriptionTier: 'free' | 'pro' | 'lifetime';
  polarCustomerId: string | null;
  subscriptionId: string | null;
  subscriptionStartedAt: string | null;
  subscriptionExpiresAt: string | null;
}

interface Subscription {
  id: string;
  status: string;
  currentPeriodEnd?: string;
  productId: string;
}

// Sidebar Navigation Item
interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  badge?: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
}

function NavItem({ icon, label, active, badge, onClick, variant = 'default' }: NavItemProps) {
  return (
    <button
      onClick={onClick}
      className={`ss-press w-full flex h-[34px] items-center gap-2.5 px-2.5 text-[13.5px] rounded-md transition-colors ${
        active
          ? 'bg-muted font-semibold text-foreground'
          : variant === 'danger'
          ? 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
          : 'text-foreground/90 hover:bg-muted'
      }`}
    >
      <span className={`flex-none ${active ? 'text-primary' : 'opacity-70'}`}>{icon}</span>
      <span className="truncate">{label}</span>
      {badge && (
        <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase">
          {badge}
        </span>
      )}
    </button>
  );
}

// Profile Settings Section
function ProfileSection({ user, onUpdate }: { 
  user: { name?: string; email?: string; image?: string | null } | null;
  onUpdate: () => void;
}) {
  const { updateUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus('idle');

    try {
      await updateUser({ name });
      setSaveStatus('success');
      onUpdate(); // Refresh user data
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = name !== (user?.name || '');

  return (
    <div>
      <h2 className="text-2xl font-bold text-foreground mb-1">Profile Settings</h2>
      <div className="h-px bg-border mb-6" />
      
      <div className="space-y-6">
        {/* Profile Picture */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Profile Picture</label>
          <div className="flex items-center gap-4">
            <ProfilePictureUpload 
              currentImage={user?.image}
              userName={user?.name}
              size="lg"
              showButton={false}
            />
            <div>
              <p className="text-sm text-muted-foreground">Click on your avatar to upload a new photo</p>
              <p className="text-xs text-muted-foreground mt-1">JPG, PNG or GIF. Max 2MB</p>
            </div>
          </div>
        </div>

        {/* Name */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Display Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full max-w-md px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
          />
        </div>

        {/* Email (read-only) */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Email</label>
          <input
            type="email"
            value={user?.email || ''}
            disabled
            className="w-full max-w-md px-3 py-2 rounded-lg border border-border bg-muted text-muted-foreground cursor-not-allowed"
          />
          <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
        </div>

        {/* Save Button */}
        <div className="flex items-center gap-3 pt-4">
          <button
            onClick={handleSave}
            disabled={!hasChanges || isSaving || !name.trim()}
            className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
          
          {saveStatus === 'success' && (
            <span className="flex items-center gap-1 text-sm text-emerald-500">
              <Check className="w-4 h-4" />
              Profile updated successfully
            </span>
          )}
          
          {saveStatus === 'error' && (
            <span className="flex items-center gap-1 text-sm text-destructive">
              <X className="w-4 h-4" />
              Failed to update profile
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

// Password Section
function PasswordSection() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus(null);
    if (newPassword.length < 8) {
      setStatus({ type: 'error', message: 'New password must be at least 8 characters.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setStatus({ type: 'error', message: 'New passwords do not match.' });
      return;
    }
    setIsSaving(true);
    try {
      const { error } = await authClient.changePassword({
        currentPassword,
        newPassword,
        revokeOtherSessions: true,
      });
      if (error) {
        setStatus({ type: 'error', message: error.message || 'Could not update password — check your current password.' });
      } else {
        setStatus({ type: 'success', message: 'Password updated successfully.' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch {
      setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-foreground mb-1">Password</h2>
      <div className="h-px bg-border mb-6" />

      <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
        {/* Current Password */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Current Password</label>
          <div className="relative">
            <input
              type={showCurrent ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
              className="w-full px-3 py-2 pr-10 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
            />
            <button
              type="button"
              onClick={() => setShowCurrent(!showCurrent)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* New Password */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">New Password</label>
          <div className="relative">
            <input
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              className="w-full px-3 py-2 pr-10 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Confirm Password */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Confirm New Password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
          />
        </div>

        {status && (
          <p className={`text-sm ${status.type === 'success' ? 'text-emerald-500' : 'text-destructive'}`}>
            {status.message}
          </p>
        )}

        <button
          type="submit"
          disabled={isSaving || !currentPassword || !newPassword || !confirmPassword}
          className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
        >
          {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
          {isSaving ? 'Updating…' : 'Update Password'}
        </button>
      </form>
    </div>
  );
}

// Billings Section
function BillingsSection({ onUpgrade }: { onUpgrade: () => void }) {
  const { isAuthenticated, user, refreshSession } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);

  // Get current user's subscription tier
  const currentTier = subscriptionStatus?.subscriptionTier || 
    (user as { subscriptionTier?: string } | null)?.subscriptionTier || 'free';
  
  const subscriptionExpiresAt = subscriptionStatus?.subscriptionExpiresAt;
  
  const activeSubscription = subscriptions.find(sub => sub.status?.toLowerCase() === 'active') || 
                             subscriptions.find(sub => sub.status?.toLowerCase() === 'canceling') ||
                             subscriptions.find(sub => sub.status?.toLowerCase() === 'canceled') ||
                             (subscriptions.length > 0 ? subscriptions[0] : null);
  const subscriptionStatusValue = activeSubscription?.status?.toLowerCase();
  const isCanceling = currentTier === 'pro' && 
    (subscriptionStatusValue === 'canceled' || subscriptionStatusValue === 'canceling');

  const getTierIcon = () => {
    switch (currentTier.toLowerCase()) {
      case 'pro':
        return <Crown className="w-5 h-5" />;
      case 'lifetime':
        return <Infinity className="w-5 h-5" />;
      default:
        return <Zap className="w-5 h-5" />;
    }
  };

  const getTierGradient = () => {
    switch (currentTier.toLowerCase()) {
      case 'pro':
        return 'from-violet-500 to-purple-600';
      case 'lifetime':
        return 'from-amber-500 to-orange-600';
      default:
        return 'from-slate-500 to-slate-600';
    }
  };

  const getTierName = () => {
    return currentTier.charAt(0).toUpperCase() + currentTier.slice(1);
  };

  // Sync subscription status from Polar
  const syncSubscription = useCallback(async (shouldRefreshSession = false) => {
    if (!isAuthenticated) return;
    
    setIsSyncing(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/subscription/sync`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to sync subscription');
      }
      
      const data = await response.json();
      setSubscriptionStatus(data);
      
      if (shouldRefreshSession && refreshSession) {
        await refreshSession();
      }
    } catch (err) {
      console.error('Error syncing subscription:', err);
    } finally {
      setIsSyncing(false);
    }
  }, [isAuthenticated, refreshSession]);

  // Fetch subscriptions on mount
  useEffect(() => {
    let isMounted = true;
    
    async function fetchData() {
      if (!isAuthenticated) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      await syncSubscription(false);
      
      const userWithPolarId = user as { polarCustomerId?: string | null } | null;
      const hasPolarCustomerId = subscriptionStatus?.polarCustomerId || userWithPolarId?.polarCustomerId;
      
      if (hasPolarCustomerId) {
        try {
          const result = await customer.subscriptions.list({
            query: { page: 1, limit: 10 },
          });
          if (isMounted && result.data) {
            setSubscriptions(result.data as unknown as Subscription[]);
          }
        } catch (err) {
          console.error('Error fetching subscriptions:', err);
          if (isMounted) setSubscriptions([]);
        }
      } else {
        if (isMounted) setSubscriptions([]);
      }
      
      if (isMounted) setIsLoading(false);
    }

    fetchData();
    return () => { isMounted = false; };
    // syncSubscription/user/polarCustomerId intentionally excluded: including
    // them would re-run this fetch in a loop. Runs only on auth-state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    setError(null);
    
    try {
      await customer.portal();
    } catch (err) {
      console.error('Portal error:', err);
      setError('Failed to open customer portal. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
        <p className="text-sm text-muted-foreground">Loading subscription info...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-2xl font-bold text-foreground">Billings</h2>
        <button
          onClick={() => syncSubscription(true)}
          disabled={isSyncing}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-muted text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
          {isSyncing ? 'Syncing...' : 'Refresh'}
        </button>
      </div>
      <div className="h-px bg-border mb-6" />

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {error}
        </div>
      )}
      
      {/* Current Plan Card */}
      <div className="rounded-xl p-5 mb-6 border border-border bg-muted/30">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getTierGradient()} flex items-center justify-center text-white`}>
              {getTierIcon()}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg font-semibold text-foreground">Current plan: {getTierName()}</span>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border border-border text-muted-foreground uppercase">
                  {currentTier}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {currentTier === 'free' 
                  ? 'Upgrade your plan to get access to all the features'
                  : currentTier === 'lifetime'
                    ? 'Lifetime access - No recurring payments'
                    : 'Full access to all features'}
              </p>
              
              {/* Subscription status */}
              {currentTier !== 'free' && (
                <div className="flex items-center gap-2 mt-2 text-sm">
                  {currentTier === 'lifetime' ? (
                    <>
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Never expires</span>
                    </>
                  ) : isCanceling && subscriptionExpiresAt ? (
                    <>
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                      <span className="text-amber-500">
                        Cancels on {new Date(subscriptionExpiresAt).toLocaleDateString()}
                      </span>
                    </>
                  ) : subscriptionExpiresAt ? (
                    <>
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        Renews on {new Date(subscriptionExpiresAt).toLocaleDateString()}
                      </span>
                    </>
                  ) : null}
                </div>
              )}
            </div>
          </div>
          
          {currentTier === 'free' && (
            <button
              onClick={onUpgrade}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
            >
              <ArrowUpRight className="w-4 h-4" />
              Upgrade Plan
            </button>
          )}
        </div>
      </div>

      {/* Manage Subscription */}
      {currentTier !== 'free' && (
        <button
          onClick={handleOpenPortal}
          disabled={portalLoading}
          className="flex items-center gap-4 w-full p-4 mb-6 rounded-xl border border-border hover:border-primary/50 transition-colors text-left"
        >
          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
            {portalLoading ? (
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            ) : (
              <Settings className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-foreground">Manage Subscription</h3>
            <p className="text-sm text-muted-foreground">View invoices, update payment method</p>
          </div>
          <ExternalLink className="w-4 h-4 text-muted-foreground" />
        </button>
      )}

      {/* Invoice History */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-1">Invoice History</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Your complete payment history in our service
        </p>
        <p className="text-sm text-muted-foreground">Invoice history is empty</p>
      </div>
    </div>
  );
}

const DEFAULT_MODELS: Partial<Record<AIProvider, string>> = {
  gemini: 'gemini-2.5-flash',
  openai: 'gpt-4o',
  anthropic: 'claude-3-5-sonnet-20241022',
};

/** Lazily fetches a provider's model catalog (once per mount of the consumer). */
function useModelCatalog(provider: AIProvider, apiKey: string) {
  const [models, setModels] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);

  const load = useCallback(async () => {
    if (fetched || loading) return;
    setLoading(true);
    setError(null);
    const res = await fetchProviderModels(provider, apiKey);
    setModels(res.models);
    setError(res.error || null);
    setLoading(false);
    setFetched(true);
  }, [provider, apiKey, fetched, loading]);

  return { models, loading, error, load };
}

/** Shared search + catalog list body for the model popovers. */
function ModelListPopoverContent({
  query,
  onQueryChange,
  onCommit,
  models,
  loading,
  error,
  showFreeText,
  selected,
}: {
  query: string;
  onQueryChange: (q: string) => void;
  onCommit: (model: string) => void;
  models: string[];
  loading: boolean;
  error: string | null;
  showFreeText: boolean;
  selected?: string;
}) {
  return (
    <PopoverContent align="start" className="w-[320px] p-0">
      <div className="border-b border-border p-2">
        <input
          autoFocus
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && query.trim()) onCommit(query);
          }}
          placeholder="Search or type a model id…"
          className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-primary"
        />
      </div>
      <div className="max-h-[240px] overflow-y-auto p-1">
        {loading ? (
          <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading models…
          </div>
        ) : (
          <>
            {error && (
              <p className="px-2 py-1.5 text-xs text-amber-600 dark:text-amber-400">
                Couldn’t fetch the list ({error}). Type a model id above.
              </p>
            )}
            {showFreeText && (
              <button
                type="button"
                onClick={() => onCommit(query)}
                className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
              >
                Use “<span className="font-mono">{query.trim()}</span>”
              </button>
            )}
            {models.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => onCommit(m)}
                className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left font-mono text-[13px] hover:bg-muted ${
                  m === selected ? 'text-primary' : 'text-foreground'
                }`}
              >
                <span className="truncate">{m}</span>
                {m === selected && <Check className="h-3.5 w-3.5 flex-none" />}
              </button>
            ))}
            {!error && !showFreeText && models.length === 0 && (
              <p className="px-2 py-3 text-sm text-muted-foreground">No models found.</p>
            )}
          </>
        )}
      </div>
    </PopoverContent>
  );
}

/** Searchable model picker: fetches the provider's catalog, with free-text fallback. */
function ModelCombobox({
  provider,
  apiKey,
  value,
  onChange,
}: {
  provider: AIProvider;
  apiKey: string;
  value: string;
  onChange: (model: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { models, loading, error, load } = useModelCatalog(provider, apiKey);

  const onOpenChange = (o: boolean) => {
    setOpen(o);
    if (o) load();
  };

  const commit = (m: string) => {
    onChange(m.trim());
    setQuery('');
    setOpen(false);
  };

  const q = query.trim().toLowerCase();
  const filtered = (q ? models.filter((m) => m.toLowerCase().includes(q)) : models).slice(0, 80);
  const showFreeText = Boolean(q) && !models.some((m) => m.toLowerCase() === q);
  const fallback = DEFAULT_MODELS[provider];

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm hover:border-primary/50"
        >
          <span className="truncate font-mono text-[13px] text-foreground">
            {value || fallback || 'Select a model'}
            {!value && fallback && <span className="ml-1.5 font-sans text-muted-foreground">(default)</span>}
          </span>
          <ChevronDown className="h-4 w-4 flex-none text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <ModelListPopoverContent
        query={query}
        onQueryChange={setQuery}
        onCommit={commit}
        models={filtered}
        loading={loading}
        error={error}
        showFreeText={showFreeText}
        selected={value}
      />
    </Popover>
  );
}

/** Free-text + catalog "add model" popover for the custom provider's saved list. */
function AddModelPopover({ apiKey, onAdd }: { apiKey: string; onAdd: (model: string) => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { models, loading, error, load } = useModelCatalog('custom', apiKey);

  const onOpenChange = (o: boolean) => {
    setOpen(o);
    if (o) load();
  };

  const commit = (m: string) => {
    const id = m.trim();
    if (!id) return;
    onAdd(id);
    setQuery('');
    setOpen(false);
  };

  const q = query.trim().toLowerCase();
  const filtered = (q ? models.filter((m) => m.toLowerCase().includes(q)) : models).slice(0, 80);
  const showFreeText = Boolean(q) && !models.some((m) => m.toLowerCase() === q);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex flex-none items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add model
        </button>
      </PopoverTrigger>
      <ModelListPopoverContent
        query={query}
        onQueryChange={setQuery}
        onCommit={commit}
        models={filtered}
        loading={loading}
        error={error}
        showFreeText={showFreeText}
      />
    </Popover>
  );
}

/** Saved-model manager for the custom provider: add and remove; the model used
 * last in the editor's AI panel is the default — there is no manual "active" control. */
function CustomModelManager({ apiKey }: { apiKey: string }) {
  const [models, setModels] = useState<string[]>(() => getCustomModels());

  const handleAdd = (id: string) => {
    addCustomModel(id);
    setModels(getCustomModels());
  };

  const handleRemove = (id: string) => {
    removeCustomModel(id);
    setModels(getCustomModels());
  };

  return (
    <div className="mt-3 pt-3 border-t border-border/60">
      <div className="mb-2 flex items-center justify-between gap-2">
        <label className="text-xs font-medium text-muted-foreground">Models</label>
        <AddModelPopover apiKey={apiKey} onAdd={handleAdd} />
      </div>
      <p className="mb-2 text-xs text-muted-foreground">
        Save every model you use — you switch between them in the editor&rsquo;s AI panel, and the last one you used becomes the default.
      </p>
      <div className="space-y-1.5">
        {models.map((id) => (
          <div
            key={id}
            className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2"
          >
            <span className="min-w-0 truncate font-mono text-[13px] text-foreground">{id}</span>
            <button
              type="button"
              onClick={() => handleRemove(id)}
              aria-label={`Remove ${id}`}
              className="flex-none text-muted-foreground transition-colors hover:text-destructive"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
        {models.length === 0 && (
          <p className="rounded-lg border border-dashed border-border px-3 py-3 text-center text-xs text-muted-foreground">
            No models yet — add one above (e.g. <code>z-ai/glm-4.6</code>).
          </p>
        )}
      </div>
    </div>
  );
}

function AISection() {
  const [expandedProvider, setExpandedProvider] = useState<AIProvider | null>(null);
  const [apiKeys, setApiKeys] = useState<Record<string, string>>({});
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [removing, setRemoving] = useState<Record<string, boolean>>({});
  const [status, setStatus] = useState<Record<string, { type: 'success' | 'error' | null; message: string }>>({});
  const [defaultProvider, setDefaultProviderState] = useState<AIProvider | null>(null);
  const [selectedModels, setSelectedModels] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadApiKeys = async () => {
      const keys: Record<string, string> = {};
      for (const provider of Object.keys(AI_PROVIDERS) as AIProvider[]) {
        if (hasApiKey(provider)) {
          const key = await getApiKey(provider);
          if (key) keys[provider] = key;
        }
      }
      setApiKeys(keys);
      setDefaultProviderState(getDefaultProvider());
      const models: Record<string, string> = {};
      for (const provider of Object.keys(AI_PROVIDERS) as AIProvider[]) {
        const m = getSelectedModel(provider);
        if (m) models[provider] = m;
      }
      setSelectedModels(models);
    };
    loadApiKeys();
  }, []);

  const handleSetKey = async (provider: AIProvider, key: string, extra?: { baseUrl?: string; model?: string }) => {
    if (!validateApiKeyFormat(provider, key)) {
      setStatus(prev => ({
        ...prev,
        [provider]: { type: 'error', message: 'Invalid API key format' }
      }));
      return;
    }

    // The custom OpenAI-compatible provider also needs a base URL + model id.
    if (provider === 'custom') {
      if (!extra?.model?.trim()) {
        setStatus(prev => ({
          ...prev,
          [provider]: { type: 'error', message: 'A model id is required (e.g. "openai/gpt-4o").' }
        }));
        return;
      }
      setCustomBaseUrl(extra.baseUrl?.trim() || DEFAULT_CUSTOM_BASE_URL);
      setCustomModel(extra.model);
    }

    setSaving(prev => ({ ...prev, [provider]: true }));
    setStatus(prev => ({ ...prev, [provider]: { type: null, message: '' } }));

    try {
      await setApiKey(provider, key);
      setApiKeys(prev => ({ ...prev, [provider]: key }));
      setStatus(prev => ({
        ...prev,
        [provider]: { type: 'success', message: 'API key saved successfully' }
      }));
      setExpandedProvider(null);

      const configured = Object.keys(apiKeys).length + 1;
      if (configured === 1 || !getStoredDefaultProvider()) {
        setDefaultProviderState(provider);
        setDefaultProvider(provider);
      }
    } catch {
      setStatus(prev => ({
        ...prev,
        [provider]: { type: 'error', message: 'Failed to save API key' }
      }));
    } finally {
      setSaving(prev => ({ ...prev, [provider]: false }));
    }
  };

  const handleRemoveKey = async (provider: AIProvider) => {
    setRemoving(prev => ({ ...prev, [provider]: true }));
    try {
      removeApiKey(provider);
      setApiKeys(prev => {
        const next = { ...prev };
        delete next[provider];
        return next;
      });
      setShowKeys(prev => {
        const next = { ...prev };
        delete next[provider];
        return next;
      });

      if (defaultProvider === provider) {
        const newDefault = getDefaultProvider();
        setDefaultProviderState(newDefault);
      }
    } finally {
      setRemoving(prev => ({ ...prev, [provider]: false }));
    }
  };

  const handleSetDefault = (provider: AIProvider) => {
    setDefaultProvider(provider);
    setDefaultProviderState(provider);
  };

  const availableProviders = Object.entries(AI_PROVIDERS);
  const configuredProviders = Object.keys(apiKeys).filter(p => hasApiKey(p as AIProvider)) as AIProvider[];
  const showDefaultSelector = configuredProviders.length >= 2;

  return (
    <div>
      <h2 className="text-2xl font-bold text-foreground mb-1">AI Configuration</h2>
      <div className="h-px bg-border mb-6" />

      <div className="mb-6 p-4 rounded-lg bg-muted/50 border border-border">
        <div className="flex items-start gap-3">
          <Brain className="w-5 h-5 text-primary mt-0.5" />
          <div>
            <h3 className="font-medium text-foreground mb-1">Bring Your Own AI Key</h3>
            <p className="text-sm text-muted-foreground">
              Configure your own API keys to power AI diagram generation. Your keys are encrypted and stored locally on your device - we never see or store them on our servers.
            </p>
          </div>
        </div>
      </div>

      {showDefaultSelector && (
        <div className="mb-6 p-4 rounded-lg bg-primary/5 border border-primary/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Star className="w-5 h-5 text-primary" />
              <div>
                <h3 className="font-medium text-foreground">Default AI Provider</h3>
                <p className="text-sm text-muted-foreground">
                  Choose which AI provider to use for diagram generation
                </p>
              </div>
            </div>
            <Select
              value={defaultProvider || ''}
              onValueChange={(v) => handleSetDefault(v as AIProvider)}
              className="w-[200px] flex-none"
            >
              <SelectTrigger>
                <SelectValue placeholder="Select provider" />
              </SelectTrigger>
              <SelectContent>
                {configuredProviders.map(provider => (
                  <SelectItem key={provider} value={provider}>
                    {AI_PROVIDERS[provider].name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {availableProviders.map(([id, provider]) => {
          const providerId = id as AIProvider;
          const isConfigured = hasApiKey(providerId);
          const isExpanded = expandedProvider === providerId;
          const apiKey = apiKeys[providerId];
          const isSaving = saving[providerId];
          const isRemoving = removing[providerId];
          const showKey = showKeys[providerId];
          const statusInfo = status[providerId];

          return (
            <div key={providerId} className="rounded-xl border border-border overflow-hidden">
              <div className="flex items-center justify-between p-4 bg-muted/30">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isConfigured ? 'bg-emerald-500/10 text-emerald-500' : 'bg-muted text-muted-foreground'
                  }`}>
                    {isConfigured ? <Check className="w-5 h-5" /> : <Brain className="w-5 h-5" />}
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">{provider.name}</h3>
                    <p className="text-sm text-muted-foreground">{provider.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isConfigured ? (
                    <span className="text-xs font-medium px-2 py-1 rounded bg-emerald-500/10 text-emerald-500">
                      Configured
                    </span>
                  ) : (
                    <button
                      onClick={() => setExpandedProvider(isExpanded ? null : providerId)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Key
                    </button>
                  )}
                </div>
              </div>

              {isExpanded && (
                <div className="p-4 border-t border-border bg-card">
                  <APIKeyInput
                    provider={providerId}
                    onSave={handleSetKey}
                    onCancel={() => setExpandedProvider(null)}
                    isSaving={isSaving}
                  />
                  {statusInfo?.message && (
                    <p className={`mt-3 text-sm ${
                      statusInfo.type === 'error' ? 'text-destructive' : 'text-emerald-500'
                    }`}>
                      {statusInfo.message}
                    </p>
                  )}
                </div>
              )}

              {isConfigured && apiKey && (
                <div className="p-4 border-t border-border bg-card">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <code className="text-sm text-muted-foreground font-mono break-all leading-relaxed">
                        {showKey ? apiKey : maskApiKey(apiKey)}
                      </code>
                      <button
                        onClick={() => setShowKeys(prev => ({ ...prev, [providerId]: !showKey }))}
                        className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                      >
                        {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <button
                      onClick={() => handleRemoveKey(providerId)}
                      disabled={isRemoving}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50 flex-shrink-0"
                    >
                      {isRemoving ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                      Remove
                    </button>
                  </div>

                  {/* Model picker — multi-model manager for OpenRouter/custom, single picker otherwise */}
                  {providerId === 'custom' ? (
                    <CustomModelManager apiKey={apiKey} />
                  ) : (
                    <div className="mt-3 pt-3 border-t border-border/60">
                      <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Model</label>
                      <ModelCombobox
                        provider={providerId}
                        apiKey={apiKey}
                        value={selectedModels[providerId] || ''}
                        onChange={(m) => {
                          setSelectedModel(providerId, m);
                          setSelectedModels((prev) => ({ ...prev, [providerId]: m }));
                        }}
                      />
                    </div>
                  )}

                  {providerId === 'custom' && (
                    <p className="mt-2 break-all text-xs text-muted-foreground font-mono">
                      <span className="text-foreground/70">Base URL:</span> {getCustomBaseUrl()}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function APIKeyInput({
  provider,
  onSave,
  onCancel,
  isSaving,
}: {
  provider: AIProvider;
  onSave: (provider: AIProvider, key: string, extra?: { baseUrl?: string; model?: string }) => void;
  onCancel: () => void;
  isSaving: boolean;
}) {
  const [key, setKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const config = AI_PROVIDERS[provider];
  const isCustom = Boolean(config.isCustom);
  const [baseUrl, setBaseUrl] = useState(getCustomBaseUrl());
  const [model, setModel] = useState(getCustomModel());

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium text-foreground mb-2 block">
          Enter your {config.name} API Key
        </label>
        <div className="relative">
          <input
            type={showKey ? 'text' : 'password'}
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder={config.keyPlaceholder}
            className="w-full px-3 py-2 pr-10 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors font-mono text-sm truncate"
          />
          <button
            type="button"
            onClick={() => setShowKey(!showKey)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
          <p className="text-xs text-muted-foreground mt-2">
            Get your API key from{' '}
            <a
              href={config.docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline inline-flex items-center gap-1"
            >
              {provider === 'gemini' ? 'Google AI Studio' : provider === 'openai' ? 'OpenAI Platform' : provider === 'anthropic' ? 'Anthropic Console' : 'OpenRouter'}
              <ExternalLink className="w-3 h-3" />
            </a>
          </p>
      </div>

      {isCustom && (
        <>
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Base URL
            </label>
            <input
              type="text"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder={DEFAULT_CUSTOM_BASE_URL}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Any OpenAI-compatible <code>/chat/completions</code> endpoint. Defaults to OpenRouter.
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Model
            </label>
            <input
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="openai/gpt-4o"
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-2">
              The provider's model id — e.g. an{' '}
              <a href="https://openrouter.ai/models" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                OpenRouter model
              </a>{' '}
              like <code>openai/gpt-4o</code> or <code>anthropic/claude-3.5-sonnet</code>.
            </p>
          </div>
        </>
      )}

      <div className="flex items-center gap-3">
        <button
          onClick={() => onSave(provider, key, isCustom ? { baseUrl, model } : undefined)}
          disabled={!key.trim() || isSaving || (isCustom && !model.trim())}
          className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              Save API Key
            </>
          )}
        </button>
        <button
          onClick={onCancel}
          disabled={isSaving}
          className="px-4 py-2 text-sm font-medium rounded-lg text-muted-foreground hover:text-foreground transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

interface SupportLinkProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
  href?: string;
  onClick?: () => void;
  external?: boolean;
}

function SupportLink({ icon, title, description, action, href, onClick, external }: SupportLinkProps) {
  const content = (
    <div className="flex items-center gap-4 p-4 rounded-xl border border-border hover:border-primary/50 hover:bg-muted/50 transition-all cursor-pointer group">
      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground group-hover:text-foreground group-hover:bg-muted/80 transition-colors">
        {icon}
      </div>
      <div className="flex-1">
        <h3 className="font-medium text-foreground group-hover:text-primary transition-colors">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {action && <div>{action}</div>}
      {!action && external && <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />}
      {!action && !external && <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />}
    </div>
  );

  if (href) {
    return (
      <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined} className="block">
        {content}
      </a>
    );
  }

  if (onClick) {
    return <button onClick={onClick} className="w-full text-left">{content}</button>;
  }

  return content;
}

function SupportSection() {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'bug' | 'feature' | 'other'>('other');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackStatus, setFeedbackStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const handleSendFeedback = async () => {
    if (!feedbackMessage.trim()) return;

    setFeedbackStatus('sending');

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:4000'}/api/feedback`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: feedbackType,
          message: feedbackMessage,
        }),
      });

      if (response.ok) {
        setFeedbackStatus('sent');
        setFeedbackMessage('');
        setTimeout(() => setFeedbackStatus('idle'), 3000);
      } else {
        setFeedbackStatus('error');
        setTimeout(() => setFeedbackStatus('idle'), 3000);
      }
    } catch {
      setFeedbackStatus('error');
      setTimeout(() => setFeedbackStatus('idle'), 3000);
    }
  };

  const feedbackEmail = 'support@stacksketch.site';
  const docsUrl = 'https://stacksketch.site/docs';

  return (
    <div className="overflow-y-auto max-h-[calc(100vh-200px)] pr-2">
      <h2 className="text-2xl font-bold text-foreground mb-1">Support</h2>
      <div className="h-px bg-border mb-6" />

      {!feedbackOpen ? (
        <>
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-foreground mb-1">Get Help</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Find answers or get help with using StackSketch
            </p>
            <div className="grid gap-3">
              <SupportLink
                icon={<FileText className="w-5 h-5" />}
                title="Documentation"
                description="Learn how to use StackSketch with our guides"
                href={docsUrl}
                external
              />
              <SupportLink
                icon={<MessageCircle className="w-5 h-5" />}
                title="FAQ"
                description="Frequently asked questions and answers"
                onClick={() => window.open(`${docsUrl}/faq`, '_blank')}
              />
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">Feedback & Support</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Help us improve or get direct help from our team
            </p>
            <div className="grid gap-3">
              <SupportLink
                icon={<Bug className="w-5 h-5" />}
                title="Report a Bug"
                description="Found something broken? Let us know"
                onClick={() => {
                  setFeedbackType('bug');
                  setFeedbackOpen(true);
                }}
              />
              <SupportLink
                icon={<Star className="w-5 h-5" />}
                title="Request a Feature"
                description="Have an idea? We'd love to hear it"
                onClick={() => {
                  setFeedbackType('feature');
                  setFeedbackOpen(true);
                }}
              />
              <SupportLink
                icon={<Mail className="w-5 h-5" />}
                title="Contact Support"
                description="Get help from our team directly"
                href={`mailto:${feedbackEmail}`}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="max-w-xl">
          <button
            onClick={() => {
              setFeedbackOpen(false);
              setFeedbackStatus('idle');
              setFeedbackMessage('');
            }}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
            Back to Support
          </button>

          <h3 className="text-xl font-semibold text-foreground mb-1">
            {feedbackType === 'bug' ? 'Report a Bug' : feedbackType === 'feature' ? 'Request a Feature' : 'Send Feedback'}
          </h3>
          <p className="text-sm text-muted-foreground mb-6">
            {feedbackType === 'bug'
              ? 'Describe the issue you encountered and steps to reproduce it.'
              : feedbackType === 'feature'
              ? 'Tell us about the feature you\'d like to see and why it would be helpful.'
              : 'Share your thoughts, suggestions, or questions with us.'}
          </p>

          <div className="space-y-4">
            {feedbackType !== 'other' && (
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Category</label>
                <div className="flex gap-2">
                  {(['bug', 'feature', 'other'] as const).map((type) => (
                    <button
                      key={type}
                      onClick={() => setFeedbackType(type)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                        feedbackType === type
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {type === 'bug' ? 'Bug' : type === 'feature' ? 'Feature' : 'Other'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Message</label>
              <textarea
                value={feedbackMessage}
                onChange={(e) => setFeedbackMessage(e.target.value)}
                placeholder={
                  feedbackType === 'bug'
                    ? 'Describe the bug... What were you doing when it happened?'
                    : feedbackType === 'feature'
                    ? 'Describe the feature you\'d like to see...'
                    : 'Your message...'
                }
                rows={6}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleSendFeedback}
                disabled={!feedbackMessage.trim() || feedbackStatus === 'sending'}
                className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {feedbackStatus === 'sending' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : feedbackStatus === 'sent' ? (
                  <>
                    <Check className="w-4 h-4" />
                    Sent!
                  </>
                ) : feedbackStatus === 'error' ? (
                  <>
                    <X className="w-4 h-4" />
                    Failed
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    Send Message
                  </>
                )}
              </button>

              {feedbackStatus === 'error' && (
                <span className="text-sm text-destructive">Failed to send. Please try again or email us directly.</span>
              )}
            </div>

            <p className="text-xs text-muted-foreground pt-2">
              For urgent issues, please email us directly at{' '}
              <a href={`mailto:${feedbackEmail}`} className="text-primary hover:underline">
                {feedbackEmail}
              </a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// Simple on/off switch.
function ToggleSwitch({ checked, onChange, label }: { checked: boolean; onChange: () => void; label: string }) {
  return (
    <Switch
      checked={checked}
      onCheckedChange={onChange}
      ariaLabel={label}
      className="flex-none"
    />
  );
}

// Editor preferences (per-browser).
function EditorSection() {
  const [showMinimap, setShowMinimapState] = useState(getShowMinimap());
  const [showGrid, setShowGridState] = useState(getShowGrid());
  const [snap, setSnapState] = useState(getSnapToGrid());
  const toggleMinimap = () => {
    const next = !showMinimap;
    setShowMinimapState(next);
    setShowMinimap(next);
  };
  const toggleGrid = () => {
    const next = !showGrid;
    setShowGridState(next);
    setShowGrid(next);
  };
  const toggleSnap = () => {
    const next = !snap;
    setSnapState(next);
    setSnapToGrid(next);
  };
  return (
    <div>
      <h2 className="text-2xl font-bold text-foreground mb-1">Editor</h2>
      <p className="text-muted-foreground mb-6">Personalize the diagram canvas.</p>
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card p-4">
          <div>
            <h3 className="font-medium text-foreground">Minimap</h3>
            <p className="text-sm text-muted-foreground">
              Show the minimap overview in the bottom-right corner of the editor.
            </p>
          </div>
          <ToggleSwitch checked={showMinimap} onChange={toggleMinimap} label="Show minimap" />
        </div>
        <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card p-4">
          <div>
            <h3 className="font-medium text-foreground">Dot grid</h3>
            <p className="text-sm text-muted-foreground">
              Show the background dot grid on the canvas by default.
            </p>
          </div>
          <ToggleSwitch checked={showGrid} onChange={toggleGrid} label="Show grid" />
        </div>
        <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card p-4">
          <div>
            <h3 className="font-medium text-foreground">Snap to grid</h3>
            <p className="text-sm text-muted-foreground">
              Snap nodes to the grid while dragging. Off by default — alignment guides handle spacing.
            </p>
          </div>
          <ToggleSwitch checked={snap} onChange={toggleSnap} label="Snap to grid" />
        </div>
      </div>
    </div>
  );
}

export function SettingsPage() {
  const navigate = useNavigate();
  const { user, refreshSession } = useAuth();
  const { openSettingsTab } = useTabs();
  const [activeSection, setActiveSection] = useState<SettingsSection>('profile');

  // Settings opens as an ordinary closable tab in the strip.
  useEffect(() => {
    openSettingsTab();
  }, [openSettingsTab]);

  const handleSignOut = async () => {
    trackSignOut();
    await signOut();
    navigate('/auth');
  };

  const handleProfileUpdate = async () => {
    // Refresh the session to get updated user data
    if (refreshSession) {
      await refreshSession();
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return <ProfileSection user={user} onUpdate={handleProfileUpdate} />;
      case 'password':
        return <PasswordSection />;
      case 'billings':
        return <BillingsSection onUpgrade={() => navigate('/pricing')} />;
      case 'ai':
        return <AISection />;
      case 'editor':
        return <EditorSection />;
      case 'apikeys':
        return <ApiKeysSection />;
      case 'support':
        return <SupportSection />;
      default:
        return null;
    }
  };

  return (
    <div className="flex h-full min-h-0">
      {/* Settings rail */}
      <div className="flex-none p-2">
        <nav className="flex h-full w-[224px] flex-col rounded-lg border border-border bg-sidebar py-2.5 shadow-sm">
          <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2.5">
            <div className="px-2 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">
              Settings
            </div>
                <NavItem
                  icon={
                    user?.image ? (
                      <img src={resolveAssetUrl(user.image)} alt="" className="w-5 h-5 rounded-full object-cover" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-medium">
                        {user?.name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                    )
                  }
                  label="Profile Settings"
                  active={activeSection === 'profile'}
                  onClick={() => setActiveSection('profile')}
                />
                <NavItem
                  icon={<Lock className="w-5 h-5" />}
                  label="Password"
                  active={activeSection === 'password'}
                  onClick={() => setActiveSection('password')}
                />
                <NavItem
                  icon={<CreditCard className="w-5 h-5" />}
                  label="Billings"
                  active={activeSection === 'billings'}
                  onClick={() => setActiveSection('billings')}
                />
                <NavItem
                  icon={<Brain className="w-5 h-5" />}
                  label="AI Settings"
                  active={activeSection === 'ai'}
                  onClick={() => setActiveSection('ai')}
                />
                <NavItem
                  icon={<LayoutGrid className="w-5 h-5" />}
                  label="Editor"
                  active={activeSection === 'editor'}
                  onClick={() => setActiveSection('editor')}
                />
                <NavItem
                  icon={<Key className="w-5 h-5" />}
                  label="API Keys"
                  active={activeSection === 'apikeys'}
                  onClick={() => setActiveSection('apikeys')}
                />
                <NavItem
                  icon={<HelpCircle className="w-5 h-5" />}
                  label="Support"
                  active={activeSection === 'support'}
                  onClick={() => setActiveSection('support')}
                />
          </div>

          <div className="mt-2 border-t border-border px-2.5 pt-2">
            <NavItem
              icon={<LogOut className="w-5 h-5" />}
              label="Sign Out"
              onClick={handleSignOut}
              variant="danger"
            />
          </div>
        </nav>
      </div>

      {/* Content */}
      <main className="min-h-0 min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[760px] px-6 py-8 md:px-10">
          {renderContent()}
        </div>
      </main>
    </div>
  );
}

export default SettingsPage;

